import type { BackboneService, Connector } from '../backbone.types';
import type { BbaasBgpSessionItem, BbaasListItem } from './bbaas.types';
import type { BbConnectorListItem } from './bbConnector.types';
import { apiTagsToSummaries } from 'kontrolEdge/components/Shared/NameTagEdit/useResourceTags';
import { isTagsManagerEnabled } from '@shared/config/featureFlags';

/**
 * Maps API `connectedServiceType` (keys are normalized uppercase) to UI {@link Connector} type.
 * Keep in sync with backend enum values for BB-Connector list rows.
 */
const CONNECTED_SERVICE_TYPE_TO_CONNECTOR: Record<string, Connector['type']> = {
    CLOUD: 'Cloud',
    SDWAN_HUB: 'SDhub',
    SDHUB: 'SDhub',
    SD_WAN: 'SDhub',
    'SD-WAN': 'SDhub',
    FWAAS: 'Fwaas',
    FW_AAS: 'Fwaas',
    FIREWALL: 'Fwaas',
    SDWAN: 'Sdwan',
    SD_WAN_BRANCH: 'Sdwan',
};

/** Resolve UI connector type from API `connectedServiceType` (topology, table tags, routing colors). */
export function mapConnectedServiceTypeToConnectorType(
    connectedServiceType: string | null | undefined
): Connector['type'] {
    const typeUpper = (connectedServiceType ?? '').trim().toUpperCase();
    return CONNECTED_SERVICE_TYPE_TO_CONNECTOR[typeUpper] ?? 'Port';
}

/** Normalized key for matching API `connectedServiceType` variants (dashes → underscores). */
function normalizedConnectedServiceTypeKey(connectedServiceType: string | null | undefined): string {
    return (connectedServiceType ?? '').trim().replace(/-/g, '_').toUpperCase();
}

const CONNECTED_SERVICE_TYPE_DISPLAY: Record<string, string> = {
    SDWAN_HUB: 'SDWAN Hub',
    SDHUB: 'SDWAN Hub',
    SD_WAN: 'SDWAN Hub',
    SDWAN: 'SDWAN',
    CLOUD: 'Cloud',
    PORT: 'Port',
    FWAAS: 'FWaaS',
    FW_AAS: 'FWaaS',
    FIREWALL: 'Firewall',
    SD_WAN_BRANCH: 'SD WAN Branch',
};

/**
 * Human-readable label for BB-Connector `connectedServiceType` (table Type column, filters).
 * CLOUD rows append `(provider)` from `connectedServiceDetail.cloud` when present.
 */
export function formatBbConnectorConnectedServiceTypeLabel(row: BbConnectorListItem): string {
    const raw = (row.connectedServiceType ?? '').trim();
    if (!raw) return '—';
    const key = normalizedConnectedServiceTypeKey(raw);
    let base =
        CONNECTED_SERVICE_TYPE_DISPLAY[key] ??
        key
            .split('_')
            .filter(Boolean)
            .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
            .join(' ');
    if (key === 'CLOUD') {
        const d = row.connectedServiceDetail;
        if (d && typeof d === 'object' && d.cloud != null) {
            const c = String(d.cloud).trim();
            if (c !== '') base = `${base} (${c})`;
        }
    }
    return base;
}

/**
 * Parse BBAAS legacy `tags` field into a label list. Accepts the
 * comma-separated string (`"#A, #B"`), a plain `string[]`, or the new
 * rich tag array from Tags Manager (each entry's `name` is used as the
 * label). Returns an empty list for any other shape so the chip strip
 * stays safe to render.
 */
export function parseBbaasTags(
    tags: BbaasListItem['tags'] | undefined
): string[] {
    if (tags == null) return [];
    if (Array.isArray(tags)) {
        const out: string[] = [];
        for (const t of tags) {
            if (t == null) continue;
            if (typeof t === 'string') {
                const s = t.trim();
                if (s) out.push(s);
                continue;
            }
            if (typeof t === 'object' && typeof t.name === 'string') {
                const s = t.name.trim();
                if (s) out.push(s);
            }
        }
        return out;
    }
    const s = String(tags).trim();
    if (!s) return [];
    return s.split(',').map((x) => x.trim()).filter(Boolean);
}

/** Map API list item to BackboneService for the table. */
export function mapBbaasItemToBackboneService(item: BbaasListItem): BackboneService {
    const vrfLabels =
        item.vrfs?.map((v) => v.displayName ?? v.name ?? '').filter(Boolean) ?? [];
    // Rich tags come from the new Tags Manager pivot (§5.5). Gated behind the
    // frontend flag so this surface stays byte-for-byte legacy when the
    // feature is off, independent of what the backend ships during rollout.
    // Returns [] for any non-conforming input so legacy payloads degrade too.
    const richTags = isTagsManagerEnabled() ? apiTagsToSummaries(item.tags) : [];
    // Legacy string list — `tagsLegacy` is the dedicated transitional field;
    // fall back to parsing `tags` directly when it isn't shipped yet.
    const legacyTags =
        item.tagsLegacy != null
            ? parseBbaasTags(item.tagsLegacy)
            : parseBbaasTags(item.tags);
    return {
        id: String(item.id),
        subscriptionId: item.subscriptionId ?? undefined,
        name: item.name,
        status: item.status,
        vrfs: vrfLabels,
        tags: legacyTags.length > 0 ? legacyTags : undefined,
        tagsRich: richTags.length > 0 ? richTags : undefined,
        connectorsCount: item.connectorCount,
        capacity: item.capacity,
        customer: item.customer
            ? {
                  id: item.customer.id,
                  name: item.customer.name ?? '',
                  chargebeeCustomerId:
                      item.customer.chargebeeCustomerId ?? undefined,
              }
            : undefined,
    };
}

/**
 * Normalizes `connectedServiceDetail` for UI (legacy string or structured object from API).
 */
export function formatBbConnectorServiceDetail(
    detail: BbConnectorListItem['connectedServiceDetail']
): string {
    if (detail == null || detail === '') {
        return '';
    }
    if (typeof detail === 'string') {
        return detail.trim();
    }
    const o = detail;
    const parts = [
        o.name,
        o.subscriptionId,
        o.cloud,
        Array.isArray(o.tags) ? o.tags.filter(Boolean).join(' ') : null,
    ].filter((x) => x != null && String(x).trim() !== '');
    return parts.map(String).join(' · ').trim();
}

/** Service subscription id from structured detail, else legacy root `subscriptionId`. */
export function bbConnectorServiceSubscriptionId(row: BbConnectorListItem): string {
    const d = row.connectedServiceDetail;
    if (d && typeof d === 'object' && d.subscriptionId != null) {
        const s = String(d.subscriptionId).trim();
        if (s !== '') return s;
    }
    const root = row.subscriptionId != null ? String(row.subscriptionId).trim() : '';
    return root;
}

/**
 * TEMPORARY — Topology tab design preview only.
 * Maps `GET .../bbaas/bb-connector` list rows into {@link Connector} until a dedicated topology payload exists.
 * Safe to delete or replace when `/backbones/:id/topology` (or equivalent) is implemented.
 */
export function mapBbConnectorItemToTopologyConnector(
    item: BbConnectorListItem
): Connector {
    const type = mapConnectedServiceTypeToConnectorType(item.connectedServiceType);

    const detailStr = formatBbConnectorServiceDetail(item.connectedServiceDetail);
    const cloudHint =
        typeof item.connectedServiceDetail === 'object' &&
        item.connectedServiceDetail != null &&
        'cloud' in item.connectedServiceDetail &&
        item.connectedServiceDetail.cloud
            ? String(item.connectedServiceDetail.cloud)
            : detailStr;
    const provider =
        type === 'Cloud' && cloudHint !== ''
            ? normalizeTopologyCloudProvider(cloudHint)
            : undefined;

    const vlanParsed = parseInt(String(item.vlan ?? ''), 10);
    const vlan = Number.isFinite(vlanParsed) ? vlanParsed : 0;

    const up = item.bgpStatus?.isEstablished ?? false;
    const bw = topologyBandwidthMbpsFromBbConnector(item);

    return {
        id: `bb-conn-${item.id}`,
        type,
        ref: String(item.id),
        subscriptionId: item.subscriptionId,
        label: detailStr || item.name?.trim() || `Connector ${item.id}`,
        provider,
        cloudRegion: item.location?.city?.trim() || undefined,
        cloudAccount: undefined,
        cloudVPC: undefined,
        vrf: item.vrf?.displayName ?? '',
        loc: item.location?.displayName ?? item.location?.name ?? '',
        tag: item.tags?.[0] ?? '',
        tags: item.tags ?? [],
        vlan,
        bgp: up ? 'Established' : 'Active',
        rxP: 0,
        txP: 0,
        rxLimit: null,
        txLimit: null,
        asn: 0,
        ip: item.bgpNeighborIp ?? '—',
        peerIp: item.bgpNeighborIp ?? '—',
        uptime: '—',
        kp: 0,
        hold: 0,
        md5: false,
        router: item.hostConfig?.[0]?.host ?? '',
        portCap: item.capacity ?? 0,
        bw,
        peak: bw,
        p95: bw,
        bwHist: [],
        routes: [],
        bbaasConnectorId: item.id,
    };
}

/** Align `connectedServiceDetail` with Topology tab provider logos (AWS, Azure, GCP, Oracle). */
function normalizeTopologyCloudProvider(detail: string): string {
    const u = detail.toUpperCase();
    if (u.includes('AWS')) return 'AWS';
    if (u.includes('AZURE')) return 'Azure';
    if (u.includes('GCP') || u.includes('GOOGLE')) return 'GCP';
    if (u.includes('ORACLE')) return 'Oracle';
    return detail;
}

/**
 * TEMPORARY — prefer live `bandwidth` max(in, out); if absent, fall back to committed `capacity` so cards are not all "Offline" in demos.
 */
function topologyBandwidthMbpsFromBbConnector(item: BbConnectorListItem): number {
    const b = item.bandwidth;
    if (
        b != null &&
        typeof b.inbound === 'number' &&
        typeof b.outbound === 'number' &&
        Number.isFinite(b.inbound) &&
        Number.isFinite(b.outbound)
    ) {
        return Math.max(b.inbound, b.outbound);
    }
    const c = item.capacity;
    return typeof c === 'number' && Number.isFinite(c) ? c : 0;
}

/** Sanitize a string for use as a file name (topology export). */
export function sanitizeTopologyFileName(s: string): string {
    return s.replace(/[^\w.-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

/** Fields matched by the topology search bar. */
export function connectorSearchHaystack(row: BbConnectorListItem): string {
    return [
        row.name,
        formatBbConnectorServiceDetail(row.connectedServiceDetail),
        row.connectedServiceType,
        String(row.id),
        row.subscriptionId,
        row.vrf?.displayName,
        row.location?.displayName,
        row.location?.name,
        row.bgpNeighborIp,
        row.vlan,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
}

/** Map API BGP session row to {@link Connector} for BGP sessions UI (modal uses placeholders where API has no data). */
export function mapBbaasBgpSessionToConnector(item: BbaasBgpSessionItem): Connector {
    const st = item.status?.trim().toLowerCase() ?? '';
    const up = st === 'up' || st === 'established';
    // Show a limit only when the session has a configured threshold —
    // sessions without one render the bare count (no fabricated default).
    const rxLimit = item.rxThreshold ?? null;
    const txLimit = item.txThreshold ?? null;
    const sub =
        item.subscriptionId != null && String(item.subscriptionId).trim() !== ''
            ? String(item.subscriptionId).trim()
            : null;
    return {
        id: `${item.connectorId}-${item.peerIp}`,
        type: mapConnectedServiceTypeToConnectorType(item.connectedServiceType),
        ref: String(item.connectorId),
        subscriptionId: sub,
        label: item.connectorName,
        vrf: item.vrf?.displayName ?? '',
        loc: '',
        tag: '',
        vlan: 0,
        bgp: up ? 'Established' : 'Active',
        rxP: item.rx,
        txP: item.tx,
        rxLimit,
        txLimit,
        asn: item.remoteAsn,
        localAsn: item.localAsn ?? null,
        ip:
            item.localIp != null && String(item.localIp).trim() !== ''
                ? String(item.localIp).trim()
                : '—',
        peerIp: item.peerIp,
        uptime: '—',
        kp: 0,
        hold: 0,
        md5: false,
        router: '',
        portCap: 0,
        bw: 0,
        peak: 0,
        p95: 0,
        bwHist: [],
        routes: [],
        bbaasConnectorId: item.connectorId,
    };
}
